import { supabase } from '../lib/supabase.js';
import { OrderCommunicationManager } from '../lib/orderCommunication.js';

export class OrdersManager {
  constructor() {
    this.orders = [];
    this.filteredOrders = [];
    this.selectedOrder = null;
    this.currentFilter = 'all';
    this.isLoading = false;
    this.communicationManager = new OrderCommunicationManager();
    
    // Order status options for filtering and updating
    this.orderStatuses = [
      { value: 'payment_initiated', label: 'Payment Initiated', color: 'bg-blue-100 text-blue-800' },
      { value: 'pending_pickup', label: 'Pending Pickup', color: 'bg-yellow-100 text-yellow-800' },
      { value: 'driver_dispatched_pickup', label: 'Driver Dispatched (Pickup)', color: 'bg-orange-100 text-orange-800' },
      { value: 'picked_up', label: 'Picked Up', color: 'bg-indigo-100 text-indigo-800' },
      { value: 'in_progress', label: 'In Progress', color: 'bg-purple-100 text-purple-800' },
      { value: 'ironing_complete', label: 'Ironing Complete', color: 'bg-cyan-100 text-cyan-800' },
      { value: 'ready_for_delivery', label: 'Ready for Delivery', color: 'bg-teal-100 text-teal-800' },
      { value: 'driver_dispatched_delivery', label: 'Driver Dispatched (Delivery)', color: 'bg-orange-100 text-orange-800' },
      { value: 'out_for_delivery', label: 'Out for Delivery', color: 'bg-amber-100 text-amber-800' },
      { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800' },
      { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' },
      { value: 'payment_failed', label: 'Payment Failed', color: 'bg-red-100 text-red-800' },
      { value: 'archived', label: 'Archived', color: 'bg-gray-100 text-gray-800' }
    ];

    this.paymentStatuses = [
      { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
      { value: 'succeeded', label: 'Succeeded', color: 'bg-green-100 text-green-800' },
      { value: 'failed', label: 'Failed', color: 'bg-red-100 text-red-800' },
      { value: 'canceled', label: 'Canceled', color: 'bg-gray-100 text-gray-800' }
    ];
  }

  render() {
    return `
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex justify-between items-center mb-6">
          <div>
            <h2 class="text-xl font-bold text-gray-900">Orders Management</h2>
            <p class="text-gray-600">View and manage customer orders</p>
          </div>
          <div class="flex space-x-3">
            <button 
              id="refresh-orders-btn" 
              class="bg-violet-600 text-white px-4 py-2 rounded-md hover:bg-violet-700"
            >
              Refresh Orders
            </button>
          </div>
        </div>

        <!-- Filters -->
        <div class="mb-6">
          <div class="flex flex-wrap gap-2">
            <button onclick="dashboardManager.ordersManager.setFilter('all')" 
                    class="filter-btn px-3 py-1 rounded-full text-sm font-medium ${this.currentFilter === 'all' ? 'bg-violet-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}">
              All Orders
            </button>
            <button onclick="dashboardManager.ordersManager.setFilter('pending')" 
                    class="filter-btn px-3 py-1 rounded-full text-sm font-medium ${this.currentFilter === 'pending' ? 'bg-violet-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}">
              Pending
            </button>
            <button onclick="dashboardManager.ordersManager.setFilter('in_progress')" 
                    class="filter-btn px-3 py-1 rounded-full text-sm font-medium ${this.currentFilter === 'in_progress' ? 'bg-violet-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}">
              In Progress
            </button>
            <button onclick="dashboardManager.ordersManager.setFilter('completed')" 
                    class="filter-btn px-3 py-1 rounded-full text-sm font-medium ${this.currentFilter === 'completed' ? 'bg-violet-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}">
              Completed
            </button>
            <button onclick="dashboardManager.ordersManager.setFilter('payment_succeeded')" 
                    class="filter-btn px-3 py-1 rounded-full text-sm font-medium ${this.currentFilter === 'payment_succeeded' ? 'bg-violet-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}">
              Payment Succeeded
            </button>
          </div>
        </div>

        <!-- Orders Table -->
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order #
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service Type
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody id="orders-table-body" class="bg-white divide-y divide-gray-200">
              <tr>
                <td colspan="8" class="px-6 py-4 text-center text-gray-500">
                  Loading orders...
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Order Details Modal -->
        <div id="order-details-modal" class="hidden fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div class="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div class="mt-3">
              <div class="flex justify-between items-center mb-4">
                <h3 class="text-lg font-medium text-gray-900" id="modal-order-title">Order Details</h3>
                <button 
                  id="close-modal-btn"
                  class="text-gray-400 hover:text-gray-600"
                >
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>

              <div class="space-y-6">
                <!-- Order Summary Section -->
                <div id="order-summary-section" class="bg-gray-50 p-4 rounded-lg">
                  <h4 class="font-medium text-gray-900 mb-3">Order Summary</h4>
                  <div id="order-summary-content" class="grid grid-cols-2 gap-4 text-sm">
                    <!-- Summary content will be populated by JavaScript -->
                  </div>
                </div>

                <!-- Discount Coupon Section (moved below order summary) -->
                <div id="discount-coupon-section" class="hidden bg-green-50 p-4 rounded-lg border border-green-200">
                  <h4 class="font-medium text-green-900 mb-3">💰 Discount Coupon Used</h4>
                  <div id="discount-coupon-content" class="grid grid-cols-2 gap-4 text-sm">
                    <!-- Discount content will be populated by JavaScript -->
                  </div>
                </div>

                <!-- Customer Information -->
                <div id="customer-info-section" class="bg-blue-50 p-4 rounded-lg">
                  <h4 class="font-medium text-blue-900 mb-3">Customer Information</h4>
                  <div id="customer-info-content" class="grid grid-cols-2 gap-4 text-sm">
                    <!-- Customer content will be populated by JavaScript -->
                  </div>
                </div>

                <!-- Ordered Items -->
                <div id="ordered-items-section">
                  <h4 class="font-medium text-gray-900 mb-3">Ordered Items</h4>
                  <div id="ordered-items-content" class="space-y-3">
                    <!-- Items will be populated by JavaScript -->
                  </div>
                </div>

                <!-- Communication History -->
                <div id="communication-history-section">
                  <h4 class="font-medium text-gray-900 mb-3">Communication History</h4>
                  <div id="communication-history-content">
                    <!-- History will be populated by JavaScript -->
                  </div>
                </div>

                <!-- Order Status Update -->
                <div class="bg-violet-50 p-4 rounded-lg">
                  <h4 class="font-medium text-violet-900 mb-3">Update Order Status</h4>
                  <div class="flex space-x-3">
                    <select id="new-order-status" class="flex-1 px-3 py-2 border border-gray-300 rounded-md">
                      <option value="">Select new status...</option>
                      ${this.orderStatuses.map(status => 
                        `<option value="${status.value}">${status.label}</option>`
                      ).join('')}
                    </select>
                    <button 
                      id="update-status-btn" 
                      class="bg-violet-600 text-white px-4 py-2 rounded-md hover:bg-violet-700 disabled:opacity-50"
                      disabled
                    >
                      Update Status
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  async bindEvents() {
    await this.loadOrders();

    // Refresh orders button
    document.getElementById('refresh-orders-btn').addEventListener('click', () => {
      this.loadOrders();
    });

    // Status select change
    const statusSelect = document.getElementById('new-order-status');
    if (statusSelect) {
      statusSelect.addEventListener('change', () => {
        const updateBtn = document.getElementById('update-status-btn');
        if (updateBtn) {
          const currentStatus = updateBtn.dataset.currentStatus;
          updateBtn.disabled = !statusSelect.value || statusSelect.value === currentStatus;
        }
      });
    }

    // Update status button
    const updateBtn = document.getElementById('update-status-btn');
    if (updateBtn) {
      updateBtn.addEventListener('click', async () => {
        const newStatus = statusSelect.value;
        if (newStatus && this.selectedOrder) {
          await this.updateOrderStatus(this.selectedOrder.id, newStatus);
        }
      });
    }

    // Close modal button
    const closeBtn = document.getElementById('close-modal-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.hideOrderDetails();
      });
    }

    // Close modal when clicking outside
    const modal = document.getElementById('order-details-modal');
    if (modal) {
      modal.addEventListener('click', (e) => {
        if (e.target.id === 'order-details-modal') {
          this.hideOrderDetails();
        }
      });
    }
  }

  async loadOrders() {
    this.isLoading = true;
    this.showLoadingState();

    try {
      const { data: orders, error } = await supabase
        .from('orders')
        .select(`
          *,
          customers (
            full_name,
            email,
            phone,
            address_line1,
            city,
            service_area
          ),
          applied_coupon:applied_coupon_id (
            id,
            code,
            type,
            value,
            description
          ),
          order_items (
            *,
            clothing_items (
              name,
              category
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      this.orders = orders || [];
      this.applyFilter();
    } catch (error) {
      this.showError('Error loading orders: ' + error.message);
    } finally {
      this.isLoading = false;
    }
  }

  setFilter(filter) {
    this.currentFilter = filter;
    this.applyFilter();
    
    // Update filter button styles
    document.querySelectorAll('.filter-btn').forEach(btn => {
      btn.className = btn.className.replace(/bg-violet-600 text-white/, 'bg-gray-200 text-gray-700 hover:bg-gray-300');
    });
    
    // Highlight active filter
    const activeBtn = document.querySelector(`[onclick="dashboardManager.ordersManager.setFilter('${filter}')"]`);
    if (activeBtn) {
      activeBtn.className = activeBtn.className.replace(/bg-gray-200 text-gray-700 hover:bg-gray-300/, 'bg-violet-600 text-white');
    }
  }

  applyFilter() {
    switch (this.currentFilter) {
      case 'pending':
        this.filteredOrders = this.orders.filter(order => 
          ['pending_pickup', 'driver_dispatched_pickup', 'picked_up'].includes(order.status)
        );
        break;
      case 'in_progress':
        this.filteredOrders = this.orders.filter(order => 
          ['in_progress', 'ironing_complete', 'ready_for_delivery', 'driver_dispatched_delivery', 'out_for_delivery'].includes(order.status)
        );
        break;
      case 'completed':
        this.filteredOrders = this.orders.filter(order => order.status === 'completed');
        break;
      case 'payment_succeeded':
        this.filteredOrders = this.orders.filter(order => order.payment_status === 'succeeded');
        break;
      default:
        this.filteredOrders = [...this.orders];
    }
    
    this.renderOrdersTable();
  }

  showLoadingState() {
    const tbody = document.getElementById('orders-table-body');
    tbody.innerHTML = `
      <tr>
        <td colspan="8" class="px-6 py-4 text-center text-gray-500">
          <div class="flex items-center justify-center">
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
            Loading orders...
          </div>
        </td>
      </tr>
    `;
  }

  renderOrdersTable() {
    const tbody = document.getElementById('orders-table-body');
    
    if (this.filteredOrders.length === 0) {
      tbody.innerHTML = `
        <tr>
          <td colspan="8" class="px-6 py-4 text-center text-gray-500">
            No orders found for the current filter.
          </td>
        </tr>
      `;
      return;
    }

    tbody.innerHTML = this.filteredOrders.map(order => this.renderOrderRow(order)).join('');
    this.bindRowEvents();
  }

  renderOrderRow(order) {
    const statusInfo = this.orderStatuses.find(s => s.value === order.status) || 
                      { label: order.status, color: 'bg-gray-100 text-gray-800' };
    
    const paymentStatusInfo = this.paymentStatuses.find(p => p.value === order.payment_status) || 
                              { label: order.payment_status, color: 'bg-gray-100 text-gray-800' };

    const serviceType = order.is_express_service ? '⚡ Express' : '📋 Standard';
    const createdDate = new Date(order.created_at).toLocaleDateString();
    
    return `
      <tr class="hover:bg-gray-50">
        <td class="px-6 py-4 whitespace-nowrap">
          <div class="font-medium text-blue-600">${order.order_number}</div>
        </td>
        <td class="px-6 py-4 whitespace-nowrap">
          <div class="font-medium text-gray-900">${order.customers?.full_name || 'Unknown'}</div>
          <div class="text-sm text-gray-500">${order.customers?.email || 'No email'}</div>
        </td>
        <td class="px-6 py-4 whitespace-nowrap">
          <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusInfo.color}">
            ${statusInfo.label}
          </span>
        </td>
        <td class="px-6 py-4 whitespace-nowrap">
          <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full ${paymentStatusInfo.color}">
            ${paymentStatusInfo.label}
          </span>
        </td>
        <td class="px-6 py-4 whitespace-nowrap">
          <div class="font-medium text-gray-900">$${order.total_amount}</div>
          ${order.discount_amount > 0 ? `<div class="text-xs text-green-600">$${order.discount_amount} off</div>` : ''}
        </td>
        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          ${serviceType}
        </td>
        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          ${createdDate}
        </td>
        <td class="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
          <button 
            class="view-order-btn text-blue-600 hover:text-blue-800"
            data-order-id="${order.id}"
          >
            View Details
          </button>
        </td>
      </tr>
    `;
  }

  bindRowEvents() {
    // View order buttons
    document.querySelectorAll('.view-order-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const orderId = e.target.dataset.orderId;
        this.viewOrderDetails(orderId);
      });
    });
  }

  async viewOrderDetails(orderId) {
    const order = this.orders.find(o => o.id === orderId);
    if (!order) return;

    this.selectedOrder = order;

    // Calculate total items count
    const totalItemsCount = order.order_items.reduce((sum, item) => sum + item.quantity, 0);

    // Update modal title
    document.getElementById('modal-order-title').textContent = `Order ${order.order_number} Details`;

    // Populate order summary
    const orderSummaryContent = document.getElementById('order-summary-content');
    orderSummaryContent.innerHTML = `
      <div><strong>Order Number:</strong> ${order.order_number}</div>
      <div><strong>Status:</strong> ${this.getStatusLabel(order.status)}</div>
      <div><strong>Payment Status:</strong> ${this.getPaymentStatusLabel(order.payment_status)}</div>
      <div><strong>Service Type:</strong> ${order.is_express_service ? 'Express Service ⚡' : 'Standard Service 📋'}</div>
      <div><strong>Subtotal:</strong> $${order.subtotal}</div>
      <div><strong>Delivery Fee:</strong> $${order.delivery_fee || '0.00'}</div>
     <div><strong>Tip Amount:</strong> $${parseFloat(order.tip_amount || 0).toFixed(2)}</div>
      <div><strong>Total Items:</strong> ${totalItemsCount} ${totalItemsCount === 1 ? 'item' : 'items'}</div>
      <div><strong>Total Amount:</strong> $${order.total_amount}</div>
      <div><strong>Pickup Date:</strong> ${this.formatDate(order.pickup_date)} (${this.formatTimeSlot(order.pickup_time_slot)})</div>
      <div><strong>Delivery Date:</strong> ${this.formatDate(order.delivery_date)} (${this.formatTimeSlot(order.delivery_time_slot)})</div>
      <div><strong>Created:</strong> ${new Date(order.created_at).toLocaleString()}</div>
      <div><strong>Last Updated:</strong> ${new Date(order.updated_at).toLocaleString()}</div>
    `;

    // Handle discount coupon section
    const discountSection = document.getElementById('discount-coupon-section');
    const discountContent = document.getElementById('discount-coupon-content');
    
    if (order.discount_amount > 0 && order.applied_coupon) {
      const coupon = order.applied_coupon;
      
      discountContent.innerHTML = `
        <div><strong>Coupon Code:</strong> <span class="font-mono bg-green-100 px-2 py-1 rounded">${coupon.code}</span></div>
        <div><strong>Discount Type:</strong> ${coupon.type === 'percentage' ? 'Percentage' : 'Fixed Amount'}</div>
        <div><strong>Coupon Value:</strong> ${coupon.type === 'percentage' ? `${coupon.value}% off` : `$${coupon.value} off`}</div>
        <div><strong>Discount Applied:</strong> <span class="text-green-600 font-medium">-$${order.discount_amount}</span></div>
        ${coupon.description ? `<div><strong>Description:</strong> ${coupon.description}</div>` : ''}
      `;
      discountSection.classList.remove('hidden');
    } else if (order.discount_amount > 0) {
      // Fallback for when discount exists but coupon details are missing
      discountContent.innerHTML = `
        <div><strong>Coupon Code:</strong> ${order.coupon_code || 'Unknown'}</div>
        <div><strong>Discount Applied:</strong> <span class="text-green-600 font-medium">-$${order.discount_amount}</span></div>
      `;
      discountSection.classList.remove('hidden');
    } else {
      discountSection.classList.add('hidden');
    }

    // Populate customer info
    const customerInfoContent = document.getElementById('customer-info-content');
    customerInfoContent.innerHTML = `
      <div><strong>Name:</strong> ${order.customers?.full_name || 'Unknown'}</div>
      <div><strong>Email:</strong> ${order.customers?.email || 'No email'}</div>
      <div><strong>Phone:</strong> ${order.customers?.phone || 'No phone'}</div>
      <div><strong>Address:</strong> ${order.customers?.address_line1 || 'No address'}</div>
      <div><strong>City:</strong> ${order.customers?.city || 'Unknown city'}</div>
      <div><strong>Service Area:</strong> ${this.formatServiceArea(order.customers?.service_area)}</div>
      ${order.pickup_code ? `<div><strong>Pickup Code:</strong> <span class="font-mono bg-yellow-100 px-2 py-1 rounded">${order.pickup_code}</span></div>` : ''}
      ${order.delivery_code ? `<div><strong>Delivery Code:</strong> <span class="font-mono bg-blue-100 px-2 py-1 rounded">${order.delivery_code}</span></div>` : ''}
      ${order.special_instructions ? `<div><strong>Special Instructions:</strong> ${order.special_instructions}</div>` : ''}
    `;

    // Populate ordered items
    const orderedItemsContent = document.getElementById('ordered-items-content');
    if (order.order_items && order.order_items.length > 0) {
      orderedItemsContent.innerHTML = order.order_items.map(item => `
        <div class="border border-gray-200 rounded-md p-3 bg-gray-50">
          <div class="flex justify-between items-center">
            <div>
              <div class="font-medium">${item.clothing_items?.name || 'Unknown Item'}</div>
              <div class="text-sm text-gray-600">Category: ${this.formatCategory(item.clothing_items?.category)}</div>
            </div>
            <div class="text-right">
              <div class="font-medium">Qty: ${item.quantity}</div>
              <div class="text-sm text-gray-600">$${item.unit_price} each</div>
              <div class="font-bold text-blue-600">$${item.total_price}</div>
            </div>
          </div>
        </div>
      `).join('');
    } else {
      orderedItemsContent.innerHTML = '<div class="text-gray-500">No items found</div>';
    }

    // Load communication history
    await this.loadCommunicationHistory(orderId);

    // Set current status in dropdown
    const statusSelect = document.getElementById('new-order-status');
    if (statusSelect) {
      statusSelect.value = order.status;
      const updateBtn = document.getElementById('update-status-btn');
      if (updateBtn) {
        updateBtn.dataset.currentStatus = order.status;
        updateBtn.disabled = true;
      }
    }

    // Show modal
    document.getElementById('order-details-modal').classList.remove('hidden');
  }

  async loadCommunicationHistory(orderId) {
    const historyContent = document.getElementById('communication-history-content');
    historyContent.innerHTML = '<div class="text-gray-500">Loading communication history...</div>';

    try {
      const history = await this.communicationManager.getOrderCommunications(orderId);
      
      if (history.length === 0) {
        historyContent.innerHTML = '<div class="text-gray-500">No communication history found</div>';
        return;
      }

      historyContent.innerHTML = history.map(comm => `
        <div class="border border-gray-200 rounded-md p-3 mb-2 bg-gray-50">
          <div class="flex justify-between items-start mb-2">
            <div class="flex items-center space-x-2">
              <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                comm.type === 'email' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
              }">
                ${comm.type.toUpperCase()}
              </span>
              <span class="text-sm font-medium">${comm.status}</span>
            </div>
            <span class="text-sm text-gray-500">${new Date(comm.sent_at).toLocaleString()}</span>
          </div>
          ${comm.subject ? `<div class="text-sm font-medium mb-1">Subject: ${comm.subject}</div>` : ''}
          <div class="text-sm text-gray-700">${comm.body.substring(0, 200)}${comm.body.length > 200 ? '...' : ''}</div>
          ${comm.external_id ? `<div class="text-xs text-gray-500 mt-1">ID: ${comm.external_id}</div>` : ''}
        </div>
      `).join('');
    } catch (error) {
      historyContent.innerHTML = `<div class="text-red-500">Error loading communication history: ${error.message}</div>`;
    }
  }

  hideOrderDetails() {
    document.getElementById('order-details-modal').classList.add('hidden');
    this.selectedOrder = null;
  }

  async updateOrderStatus(orderId, newStatus) {
    try {
      // Get current order status before updating
      const { data: currentOrder, error } = await supabase
        .from('orders')
        .select('status, order_number')
        .eq('id', orderId)
        .single();

      if (error) {
        console.error('Error fetching current order status:', error);
        throw error;
      }

      if (currentOrder.status === newStatus) {
        this.showError('Order is already in this status. No change made.');
        return;
      }

      const oldStatus = currentOrder.status;
      
      // Update the order status
      const { error: updateError } = await supabase
        .from('orders')
        .update({ status: newStatus })
        .eq('id', orderId);

      if (updateError) throw updateError;
      
      // Manually trigger communication after successful status update
      try {
        await this.communicationManager.triggerCommunication(orderId, newStatus, oldStatus);
        console.log(`Successfully triggered communication for order ${currentOrder.order_number}: ${oldStatus} → ${newStatus}`);
      } catch (commError) {
        console.warn('Order status updated but communication failed:', commError);
        // Don't throw error here - order update was successful
      }

      // Refresh orders and close modal
      await this.loadOrders();
      this.hideOrderDetails();
      
      this.showSuccess(`Order status updated to: ${this.getStatusLabel(newStatus)}`);
    } catch (error) {
      this.showError('Error updating order status: ' + error.message);
    }
  }

  getStatusLabel(status) {
    const statusInfo = this.orderStatuses.find(s => s.value === status);
    return statusInfo ? statusInfo.label : status;
  }

  getPaymentStatusLabel(status) {
    const statusInfo = this.paymentStatuses.find(s => s.value === status);
    return statusInfo ? statusInfo.label : status;
  }

  formatDate(dateString) {
    if (!dateString) return 'Not set';
    try {
      // Parse date components to avoid timezone issues
      const [year, month, day] = dateString.split('-').map(Number);
      // Create date in local timezone (month is 0-indexed)
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  }

  formatTimeSlot(timeSlot) {
    const timeSlotMap = {
     'afternoon': '4:00 PM - 6:00 PM',
     'evening': '6:00 PM - 8:00 PM'
    };
    return timeSlotMap[timeSlot] || timeSlot;
  }

  formatServiceArea(area) {
    if (!area) return 'Unknown';
    return area.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  formatCategory(category) {
    if (!category) return 'Unknown';
    return category.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  showError(message) {
    console.error('OrdersManager Error:', message);
    // You can implement a toast notification system here
  }

  showSuccess(message) {
    console.log('OrdersManager Success:', message);
    // You can implement a toast notification system here
  }
}